import React, { useEffect, useRef, useState } from 'react';
import { attachOlderBarLoader, captureVisibleRange, restoreVisibleRange } from '@/components/modern/chartPaging';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';
import type { PaperTradingTrade, TradeEvent } from '@/services/strategyService';

/**
 * Click-a-row → see how a trade progressed. Fetches 1-minute bars over the
 * trade window and overlays entry/exit markers plus the entry, INITIAL (wide ATR) stop
 * and CURRENT (trailing) stop as price lines — so a trade that got knocked out by a
 * tight trail far from its initial stop is immediately visible.
 *
 * Asset-aware bar source: forex/crypto read the streaming store, equity reads the bulk
 * historical store. Used by the
 * paper trade tables (equity + forex) for a consistent click-to-chart. The live automated
 * panels render only rule toggles (no per-trade rows), so there's nothing to chart there yet.
 */

interface MinuteBar { timestamp: string; open: number; high: number; low: number; close: number; }

const isJpy = (s: string) => s.toUpperCase().includes('JPY');
// Price decimals by asset class: forex JPY 3 / other 5; equity & crypto 2.
const dpFor = (t: PaperTradingTrade) =>
  (t.assetClass ?? 'Equity') === 'Forex' ? (isJpy(t.symbol) ? 3 : 5) : 2;
const dispSymbol = (s: string) => (s.length === 6 ? `${s.slice(0, 3)}/${s.slice(3, 6)}` : s);
const fmtNY = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date(iso)) + ' ET'
    : '-';

/** Each pan-back past the default buffer pulls in this many more days of bars. */
const EXTRA_DAYS_PER_PAGE = 3;
const MAX_EXTRA_DAYS = 30;

export const TradeChartDialog: React.FC<{
  trade: PaperTradingTrade | null;
  onClose: () => void;
  /** Optional in-trade event log (entry, trailing-stop changes, break-even, exit). When provided,
   *  the chart draws a STEPPED stop-price line tracking every change, and a why/event panel renders
   *  below. Paper-trading callers omit these; the Backtest Lab passes them. */
  events?: TradeEvent[];
  /** Rich entry reason ("what conditions triggered it"). */
  entryReason?: string | null;
  /** When set, bars are fetched via the anonymous token-scoped endpoint (public shared run
   *  detail — the viewer has no login). Omitted for authenticated callers. */
  shareToken?: string;
}> = ({ trade, onClose, events, entryReason, shareToken }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartApiRef = useRef<any>(null);
  const pagingHandleRef = useRef<{ detach: () => void } | null>(null);
  const [bars, setBars] = useState<MinuteBar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Extra days of context pulled in by panning back past the default buffer.
  const [extraDays, setExtraDays] = useState(0);
  // Viewport to restore after a paged refetch — this chart is rebuilt from
  // scratch whenever `bars` changes, so without this every page load throws the
  // user back to the trade window.
  const pendingRangeRef = useRef<{ from: number; to: number } | null>(null);
  const lastBarCountRef = useRef(0);

  const open = trade != null;

  // Reset paging state per trade — otherwise opening a second trade inherits the
  // first one's widened window and its "no more bars" latch.
  useEffect(() => {
    setExtraDays(0);
    lastBarCountRef.current = 0;
    pendingRangeRef.current = null;
  }, [trade?.id]);

  // Fetch 1m bars across the trade window (4h before entry → 2h after exit / now).
  useEffect(() => {
    if (!trade) { setBars([]); setError(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setBars([]);
      try {
        const entryT = new Date(trade.entryTime);
        const exitT = trade.exitTime ? new Date(trade.exitTime) : new Date();
        // Buffer a full day each side for context (the chart fetches a fixed window and doesn't
        // lazy-load on scroll, so a generous buffer is what makes the surrounding price visible).
        const DAY = 24 * 60 * 60 * 1000;
        const fromMs = entryT.getTime() - DAY * (1 + extraDays);
        const toMs = exitT.getTime() + DAY;
        // No `source`: the API serves one logical bar series and resolves the store itself.
        // Branching here meant every consumer had to know which physical table holds which asset
        // class — and the trade records don't reliably carry an asset class to branch on.
        const fromQ = encodeURIComponent(new Date(fromMs).toISOString());
        const toQ = encodeURIComponent(new Date(toMs).toISOString());
        // Public shared run detail → anonymous token-scoped endpoint (bars only for this run's
        // traded symbols). Authenticated callers hit the normal per-symbol endpoint.
        const url = shareToken
          ? `${API_BASE}/api/TimescaleDb/bars/by-share/${encodeURIComponent(shareToken)}`
            + `?symbol=${encodeURIComponent(trade.symbol)}&period=Minute&timeUnits=1&from=${fromQ}&to=${toQ}`
          : `${API_BASE}/api/TimescaleDb/bars/${trade.symbol}/timescaledb`
            + `?period=Minute&timeUnits=1&from=${fromQ}&to=${toQ}`;
        const resp = await apiFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: MinuteBar[] = await resp.json();
        if (!cancelled) setBars(data);
      } catch (e: any) {
        if (!cancelled) { setError(e?.message || String(e)); setBars([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [trade?.id, trade?.symbol, trade?.assetClass, trade?.entryTime, trade?.exitTime, shareToken, extraDays]);

  // Render candles + markers + price lines.
  useEffect(() => {
    if (!trade || !containerRef.current || !bars.length) return;
    let cancelled = false;
    (async () => {
      const lc = await import('lightweight-charts');
      const { createChart, CandlestickSeries, createSeriesMarkers, LineSeries } = lc as any;
      if (cancelled || !containerRef.current) return;

      if (chartApiRef.current) { try { chartApiRef.current.remove(); } catch { /* gone */ } chartApiRef.current = null; }

      const isDark = document.documentElement.classList.contains('dark');
      const chart = createChart(containerRef.current, {
        autoSize: true,
        layout: { background: { color: 'transparent' }, textColor: isDark ? '#d1d4dc' : '#333' },
        grid: {
          vertLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
          horzLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
        },
        timeScale: {
          timeVisible: true, secondsVisible: false,
          borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc',
          tickMarkFormatter: (t: number) => new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
          }).format(new Date(t * 1000)),
        },
        rightPriceScale: { borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        crosshair: { mode: 0 },
      });
      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350',
        borderUpColor: '#26a69a', borderDownColor: '#ef5350',
        wickUpColor: '#26a69a', wickDownColor: '#ef5350',
        priceFormat: { type: 'price', precision: dpFor(trade), minMove: Math.pow(10, -dpFor(trade)) },
      });
      series.setData(bars.map(b => ({
        time: Math.floor(new Date(b.timestamp).getTime() / 1000),
        open: b.open, high: b.high, low: b.low, close: b.close,
      })));

      const isLong = (trade.direction ?? 'Long') === 'Long';
      const realized = trade.realizedPnL ?? 0;
      const isWin = realized > 0;
      const markers: any[] = [{
        time: Math.floor(new Date(trade.entryTime).getTime() / 1000),
        position: isLong ? 'belowBar' : 'aboveBar',
        color: '#58a6ff', shape: isLong ? 'arrowUp' : 'arrowDown',
        text: `${isLong ? 'LONG' : 'SHORT'} @ ${trade.entryPrice.toFixed(dpFor(trade))}`,
      }];
      if (trade.exitTime && trade.exitPrice != null) {
        markers.push({
          time: Math.floor(new Date(trade.exitTime).getTime() / 1000),
          position: isLong ? 'aboveBar' : 'belowBar',
          color: isWin ? '#26a69a' : '#ef5350', shape: 'circle',
          text: `${trade.exitReason ?? 'exit'} @ ${trade.exitPrice.toFixed(dpFor(trade))}`,
        });
      }
      createSeriesMarkers(series, markers);

      const line = (price: number, color: string, title: string, dashed = true) =>
        series.createPriceLine({ price, color, lineWidth: 1, lineStyle: dashed ? 2 : 0, axisLabelVisible: true, title });
      line(trade.entryPrice, '#58a6ff', 'entry', false);
      if (trade.initialStopPrice) line(trade.initialStopPrice, '#ff9800', 'init stop');
      if (trade.currentStopPrice && trade.currentStopPrice !== trade.initialStopPrice)
        line(trade.currentStopPrice, '#ffeb3b', 'trail stop');
      const tp = trade.currentTakeProfitPrice ?? trade.initialTakeProfitPrice;
      if (tp != null) line(tp, '#4caf50', 'target');

      // Stepped stop-price line from the event log — shows the stop RATCHETING over the trade
      // (every trailing-stop change / break-even move), not just the static initial/final levels.
      if (events && events.length && LineSeries) {
        const byTime = new Map<number, number>();
        for (const e of events) if (e.stopPrice != null)
          byTime.set(Math.floor(new Date(e.time).getTime() / 1000), e.stopPrice);
        const stopPts = [...byTime.entries()].sort((a, b) => a[0] - b[0]).map(([time, value]) => ({ time, value }));
        if (stopPts.length > 1) {
          const stopSeries = chart.addSeries(LineSeries, {
            color: '#ffb300', lineWidth: 1, lineType: 1, // WithSteps
            priceLineVisible: false, lastValueVisible: false,
            priceFormat: { type: 'price', precision: dpFor(trade), minMove: Math.pow(10, -dpFor(trade)) },
          });
          stopSeries.setData(stopPts);
        }
      }

      // A paged refetch rebuilt the chart — put the viewport back rather than
      // refitting to the (now wider) full range.
      if (pendingRangeRef.current) {
        restoreVisibleRange(chart, pendingRangeRef.current);
        pendingRangeRef.current = null;
      } else {
        chart.timeScale().fitContent();
      }

      pagingHandleRef.current?.detach();
      pagingHandleRef.current = attachOlderBarLoader(chart, {
        onLoadOlder: async () => {
          // The previous page returned nothing new → no more bars back there.
          if (lastBarCountRef.current > 0 && bars.length <= lastBarCountRef.current) return 0;
          if (extraDays >= MAX_EXTRA_DAYS) return 0;
          lastBarCountRef.current = bars.length;
          pendingRangeRef.current = captureVisibleRange(chart);
          setExtraDays(d => Math.min(d + EXTRA_DAYS_PER_PAGE, MAX_EXTRA_DAYS));
          return 1;   // non-zero keeps the loader armed; the checks above latch it off
        },
      });

      chartApiRef.current = chart;
    })();
    return () => {
      cancelled = true;
      pagingHandleRef.current?.detach();
      pagingHandleRef.current = null;
      if (chartApiRef.current) { try { chartApiRef.current.remove(); } catch { /* gone */ } chartApiRef.current = null; }
    };
  }, [bars, trade?.id, trade?.symbol, trade?.assetClass, events, extraDays]);

  if (!trade) return null;
  const realized = trade.realizedPnL ?? 0;
  const isOpen = !trade.exitTime;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-semibold">{dispSymbol(trade.symbol)}</span>
            <span className={trade.direction === 'Short' ? 'text-red-500' : 'text-green-500'}>{trade.direction ?? 'Long'}</span>
            <span className="text-muted-foreground font-normal">entry {trade.entryPrice.toFixed(dpFor(trade))} · {fmtNY(trade.entryTime)}</span>
            {isOpen
              ? <span className="text-muted-foreground font-normal">open · mark {trade.lastPrice && trade.lastPrice > 0 ? trade.lastPrice.toFixed(dpFor(trade)) : '-'}</span>
              : <span className="font-normal">
                  <span className={realized >= 0 ? 'text-green-500' : 'text-red-500'}>
                    exit {trade.exitPrice?.toFixed(dpFor(trade)) ?? '-'} · {realized >= 0 ? '+' : ''}{realized.toFixed(1)}
                  </span>
                  <span className="text-muted-foreground"> · {trade.exitReason ?? '-'}{trade.realizedRMultiple != null ? ` · ${trade.realizedRMultiple.toFixed(2)}R` : ''}</span>
                </span>}
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <div ref={containerRef} className="w-full" style={{ height: 380 }} />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading bars…
            </div>
          )}
          {!loading && error && bars.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-destructive">Chart unavailable: {error}</div>
          )}
          {!loading && !error && bars.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">No bar data for this window.</div>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">
          1-minute bars. Blue = entry · orange dashed = initial (ATR) stop · yellow dashed = {isOpen ? 'current trailing stop' : 'trailing stop at exit'}{(trade.currentTakeProfitPrice ?? trade.initialTakeProfitPrice) != null ? ' · green dashed = target' : ''}{events && events.length ? ' · amber stepped line = stop over time' : ''}.
        </div>

        {/* Why selected + in-trade event log — only when the caller passes the trade's events. */}
        {(entryReason || (events && events.length > 0)) && (() => {
          const entryEvt = events?.find(e => (e.type || '').toLowerCase().includes('entry'));
          const sig = entryEvt?.signal;
          return (
            <div className="mt-3 space-y-2 text-xs border-t pt-2">
              {entryReason && (
                <div><span className="text-muted-foreground">Why selected: </span><span>{entryReason}</span></div>
              )}
              {sig && Object.keys(sig).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(sig).map(([k, v]) => (
                    <span key={k} className="px-1.5 py-0.5 rounded bg-muted/60 text-[10px] tabular-nums" title={k}>
                      {k}: {typeof v === 'number' ? (Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2)) : String(v)}
                    </span>
                  ))}
                </div>
              )}
              {events && events.length > 0 && (
                <div className="max-h-44 overflow-auto border rounded">
                  <table className="w-full text-[11px]">
                    <thead className="text-left text-muted-foreground border-b sticky top-0 bg-card">
                      <tr><th className="px-2 py-1">Time</th><th className="px-2 py-1">Event</th><th className="px-2 py-1 text-right">Price</th><th className="px-2 py-1 text-right">Stop</th><th className="px-2 py-1">Notes</th></tr>
                    </thead>
                    <tbody>
                      {events.map((e, i) => (
                        <tr key={i} className="border-b border-border/30">
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{fmtNY(e.time)}</td>
                          <td className="px-2 py-1">{e.type}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{e.price != null ? e.price.toFixed(dpFor(trade)) : '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{e.stopPrice != null ? e.stopPrice.toFixed(dpFor(trade)) : '—'}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[220px]" title={e.notes ?? ''}>{e.notes ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

export default TradeChartDialog;
